/**
 * Operational conflict engine — the single source of truth for "is this
 * soldier / this vehicle actually free, and allowed, for this task?".
 *
 * The question the assignment form asks before it lets a manager commit a
 * crew, answered over the whole board rather than over one task:
 *
 *  1. **TIME_OVERLAP** — the vehicle, or the soldier in any role, is already
 *     booked on another open task whose window overlaps this one. Windows are
 *     half-open, so a task starting exactly when another ends is *not* a
 *     conflict; a window whose estimated end is at or before its start is read
 *     as running past midnight.
 *  2. **LICENSE_MISMATCH** — asked of `checkDriverVehicleMatch()` and nothing
 *     else. This module never re-decides a licence question; it only translates
 *     the matcher's failures into findings the banner can render.
 *  3. **SOLDIER_STATUS** — a temporary status ("בבית עד יום שלישי 08:00") that
 *     has not expired by the time the task starts, or a plain availability that
 *     is not `available`.
 *  4. **VEHICLE_STATUS** — the vehicle side of the same question: grounded, in
 *     the workshop, or already out.
 *
 * Nothing here blocks anything. Every finding is returned so the UI can warn,
 * explain and still offer "שבץ בכל זאת" — the override, built by
 * {@link buildAssignmentOverride}, is what answers a finding.
 */
import { checkDriverVehicleMatch } from './driver-matcher';
import { effectiveAvailability, formatStatusHorizon, isTimedStatusActive } from './soldier-status';
import {
  CREW_ROLE_LABELS,
  OVERRIDE_REASON_LABELS,
  OVERRIDE_REASON_REQUIRING_TEXT,
} from '../types/assignment';
import type {
  AssignmentOverride,
  ConflictSeverity,
  ConflictSubjectType,
  CrewRole,
  OverrideReasonCode,
  ScheduleConflict,
  ScheduleConflictType,
  TaskTeam,
} from '../types/assignment';
import {
  SOLDIER_AVAILABILITY_LABELS,
  TIMED_STATUS_LABELS,
  VEHICLE_STATUS_LABELS,
} from '../types/fleet';
import type { MatchFailureCode, Soldier, Vehicle } from '../types/fleet';
import { OPEN_TASK_STATUSES, normalizeTaskStatus } from '../types/task';
import type { TaskStatusId } from '../types/task';

// ── Shapes the engine works over ─────────────────────────────────────

/** The part of a task that decides scheduling — a draft form supplies it too. */
export interface TaskWindow {
  id: string;
  title: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:MM`, 24-hour. */
  startTime: string;
  /** `HH:MM` — an estimate, and the end of the window this engine reserves. */
  estEndTime: string;
  statusId?: TaskStatusId;
  /**
   * Set once the task is in the recycle bin. A deleted task holds nobody — see
   * {@link isBookableTask} — so the bin never raises a conflict on the board.
   */
  deletedAt?: string | null;
}

/** One already-booked task and the convoy riding on it. */
export interface ScheduleEntry {
  task: TaskWindow;
  teams: readonly TaskTeam[];
}

/** Absolute milliseconds, half-open: `[start, end)`. */
export interface TimeRange {
  start: number;
  end: number;
}

/** Everything the engine needs besides the candidate itself. */
export interface ConflictContext {
  /** The task being crewed. Without a window, time is simply not checked. */
  task?: TaskWindow | null;
  /** Every other open task with a convoy on it. */
  schedule?: readonly ScheduleEntry[];
  /** The team being edited — its own bookings never conflict with itself. */
  exceptTeamId?: string;
  /** Evaluation moment, for licence expiry. Defaults to now. */
  now?: number;
}

/** The verdict for one candidate, or for a whole team. */
export interface AssignmentCheck {
  hasConflict: boolean;
  conflicts: ScheduleConflict[];
  /** The worst severity found, or `null` when everything is clear. */
  severity: ConflictSeverity | null;
  /** One line for the banner — the green sentence, or every detail joined. */
  summary: string;
}

/** What the form says when a candidate is both allowed and free. */
export const CLEAR_ASSIGNMENT_MESSAGE = 'מתאים ופנוי בזמן המבוקש';

// ── Time ─────────────────────────────────────────────────────────────

const pad = (value: number): string => String(value).padStart(2, '0');

/** `YYYY-MM-DD` + `HH:MM` as a local moment, or `NaN` when either is unusable. */
function moment(date: string, time: string): number {
  const [year, month, day] = String(date ?? '').split('-').map(Number);
  const [hour, minute] = String(time ?? '').split(':').map(Number);
  if (![year, month, day, hour, minute].every((part) => Number.isFinite(part))) return NaN;
  return new Date(year, month - 1, day, hour, minute, 0, 0).getTime();
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The window a task occupies. An estimated end at or before the start is read
 * as the small hours of the next day, so a night convoy still reserves time
 * instead of collapsing to nothing.
 */
export function taskTimeRange(task: TaskWindow | null | undefined): TimeRange | null {
  if (!task?.date || !task.startTime) return null;
  const start = moment(task.date, task.startTime);
  if (!Number.isFinite(start)) return null;
  const rawEnd = task.estEndTime ? moment(task.date, task.estEndTime) : NaN;
  if (!Number.isFinite(rawEnd)) return { start, end: start };
  return { start, end: rawEnd > start ? rawEnd : rawEnd + DAY_MS };
}

/** Half-open overlap — touching windows are not a conflict. */
export function rangesOverlap(a: TimeRange | null, b: TimeRange | null): boolean {
  if (!a || !b) return false;
  if (a.start === a.end || b.start === b.end) return false;
  return a.start < b.end && b.start < a.end;
}

/** `10:00-14:00` — the window as the warning quotes it. */
export function formatTaskWindowRange(task: TaskWindow): string {
  if (!task.startTime) return '';
  return task.estEndTime ? `${task.startTime}-${task.estEndTime}` : task.startTime;
}

/** ` (02/07)` when the clash is on another day, empty when it is the same one. */
function dateSuffix(booked: TaskWindow, against: TaskWindow): string {
  if (!booked.date || booked.date === against.date) return '';
  const [, month, day] = booked.date.split('-');
  return month && day ? ` (${pad(Number(day))}/${pad(Number(month))})` : '';
}

/**
 * A task still holds its crew only while it is on the board — i.e. while it is
 * בביצוע. Read through the normaliser, so a row an older build filed as
 * "ממתינה לשיבוץ" goes on reserving its convoy instead of silently releasing it.
 */
function isOpenTask(task: TaskWindow): boolean {
  return !task.statusId || OPEN_TASK_STATUSES.includes(normalizeTaskStatus(task.statusId));
}

/**
 * Whether a task reserves its convoy at all.
 *
 * Two ways it stops doing so, and they are not the same thing: a task can be
 * *closed* — finished or cancelled — or it can be *deleted*, sitting in the
 * recycle bin with its status untouched. A deleted task keeps its `pending`
 * stamp, so the status test alone would let a task the manager threw away go on
 * blocking the vehicle and the crew it used to hold. It does not: the bin is
 * off the board, and off the board is free.
 */
export function isBookableTask(task: TaskWindow): boolean {
  return !task.deletedAt && isOpenTask(task);
}

/** The bookings worth checking: live, dated, and not the task being crewed. */
function relevantEntries(context: ConflictContext): ScheduleEntry[] {
  const current = context.task;
  return (context.schedule ?? []).filter(
    (entry) => entry.task && entry.task.id !== current?.id && isBookableTask(entry.task),
  );
}

// ── Findings ─────────────────────────────────────────────────────────

export const conflictKey = (
  subjectType: ConflictSubjectType,
  subjectId: string,
  conflictType: ScheduleConflictType,
): string => `${subjectType}:${subjectId}:${conflictType}`;

function finding(
  subjectType: ConflictSubjectType,
  subjectId: string,
  conflictType: ScheduleConflictType,
  severity: ConflictSeverity,
  details: string,
  extra: Partial<ScheduleConflict> = {},
): ScheduleConflict {
  return {
    hasConflict: true,
    conflictType,
    severity,
    details,
    key: conflictKey(subjectType, subjectId, conflictType),
    subjectType,
    subjectId,
    ...extra,
  };
}

/** The role a soldier already holds on a booked team, or `null`. */
function roleOnTeam(team: TaskTeam, soldierId: string): CrewRole | null {
  if (team.driverId === soldierId) return 'driver';
  if (team.escortOption === 'soldier' && team.escortId === soldierId) return 'escort';
  return team.passengers.some((passenger) => passenger.soldierId === soldierId)
    ? 'passenger'
    : null;
}

// ── 1. Time overlap ──────────────────────────────────────────────────

/** Every open task whose window overlaps and already holds this vehicle. */
export function checkVehicleTimeConflicts(
  vehicleId: string,
  context: ConflictContext,
): ScheduleConflict[] {
  const current = context.task;
  const window = taskTimeRange(current);
  if (!vehicleId || !current || !window) return [];

  return relevantEntries(context).flatMap((entry) => {
    if (!rangesOverlap(window, taskTimeRange(entry.task))) return [];
    const booked = entry.teams.some(
      (team) => team.vehicleId === vehicleId && team.id !== context.exceptTeamId,
    );
    if (!booked) return [];
    const range = formatTaskWindowRange(entry.task);
    return [
      finding(
        'vehicle',
        vehicleId,
        'TIME_OVERLAP',
        'CRITICAL',
        `הרכב משובץ למשימה "${entry.task.title}" בשעות ${range}${dateSuffix(entry.task, current)}`,
        { conflictingTaskId: entry.task.id, conflictingWindow: range },
      ),
    ];
  });
}

/** The same question for a soldier, in any role — driver, escort or passenger. */
export function checkSoldierTimeConflicts(
  soldierId: string,
  context: ConflictContext,
): ScheduleConflict[] {
  const current = context.task;
  const window = taskTimeRange(current);
  if (!soldierId || !current || !window) return [];

  return relevantEntries(context).flatMap((entry) => {
    if (!rangesOverlap(window, taskTimeRange(entry.task))) return [];
    for (const team of entry.teams) {
      if (team.id === context.exceptTeamId) continue;
      const role = roleOnTeam(team, soldierId);
      if (!role) continue;
      const range = formatTaskWindowRange(entry.task);
      return [
        finding(
          'soldier',
          soldierId,
          'TIME_OVERLAP',
          'CRITICAL',
          `משובץ כ${CREW_ROLE_LABELS[role]} למשימה "${entry.task.title}" בשעות ${range}${dateSuffix(entry.task, current)}`,
          { conflictingTaskId: entry.task.id, conflictingWindow: range, crewRole: role },
        ),
      ];
    }
    return [];
  });
}

// ── 2. Licence ───────────────────────────────────────────────────────

/** Licence failures are refusals; availability is reported by its own check. */
const LICENCE_SEVERITY: Partial<Record<MatchFailureCode, ConflictSeverity>> = {
  EXPLICIT_DISALLOW: 'CRITICAL',
  NO_LICENSE: 'CRITICAL',
  LICENSE_EXPIRED: 'CRITICAL',
  MISSING_WEIGHT_CATEGORY: 'CRITICAL',
};

/**
 * The licence side of the pairing, asked of the matching engine. Availability
 * is deliberately ignored here — the status checks below own it, and reporting
 * it twice would make the manager acknowledge the same fact two ways.
 */
export function checkLicenseMatch(
  soldier: Soldier,
  vehicle: Vehicle,
  context: ConflictContext = {},
): ScheduleConflict[] {
  const match = checkDriverVehicleMatch(soldier, vehicle, {
    now: context.now ?? Date.now(),
    ignoreAvailability: true,
  });
  if (match.ok) return [];

  const details = match.failures
    .filter((failure) => LICENCE_SEVERITY[failure.code])
    .map((failure) => failure.message);
  if (details.length === 0) return [];

  const severity: ConflictSeverity = match.failures.some(
    (failure) => LICENCE_SEVERITY[failure.code] === 'CRITICAL',
  )
    ? 'CRITICAL'
    : 'WARNING';

  return [
    finding('soldier', soldier.id, 'LICENSE_MISMATCH', severity, details.join(' · '), {
      crewRole: 'driver',
    }),
  ];
}

// ── 3 & 4. Status ────────────────────────────────────────────────────

/**
 * The soldier's own state at the moment the task starts — a timed status that
 * has not lapsed yet, or a plain availability that is not `available`. A
 * status that expires *before* the task begins is no conflict at all: that is
 * the whole point of giving it an expiry.
 */
export function checkSoldierStatus(
  soldier: Soldier,
  context: ConflictContext = {},
): ScheduleConflict[] {
  const now = context.now ?? Date.now();
  const window = taskTimeRange(context.task);
  /** Judged at the start of the task, or right now when there is no window. */
  const at = window ? window.start : now;

  const availability = effectiveAvailability(soldier, at);
  if (availability === 'available') return [];

  const status = soldier.temporaryStatus;
  const timed = isTimedStatusActive(status, at) && status;
  const details = timed
    ? `${TIMED_STATUS_LABELS[timed.kind]} ${formatStatusHorizon(timed.until, now)} — הסטטוס עדיין בתוקף בתחילת המשימה${timed.note ? ` (${timed.note})` : ''}`
    : `סטטוס החייל: ${SOLDIER_AVAILABILITY_LABELS[availability]}`;

  return [finding('soldier', soldier.id, 'SOLDIER_STATUS', 'WARNING', details)];
}

/** The vehicle's own state — grounded and in-the-workshop are refusals. */
export function checkVehicleStatus(
  vehicle: Vehicle,
  _context: ConflictContext = {},
): ScheduleConflict[] {
  if (vehicle.status === 'available') return [];
  const severity: ConflictSeverity = vehicle.status === 'in_use' ? 'WARNING' : 'CRITICAL';
  return [
    finding(
      'vehicle',
      vehicle.id,
      'VEHICLE_STATUS',
      severity,
      `מצב הרכב: ${VEHICLE_STATUS_LABELS[vehicle.status]}`,
    ),
  ];
}

// ── Candidate-level checks ───────────────────────────────────────────

const verdict = (conflicts: ScheduleConflict[]): AssignmentCheck => ({
  hasConflict: conflicts.length > 0,
  conflicts,
  severity: conflicts.some((item) => item.severity === 'CRITICAL')
    ? 'CRITICAL'
    : conflicts.length > 0
      ? 'WARNING'
      : null,
  summary:
    conflicts.length === 0
      ? CLEAR_ASSIGNMENT_MESSAGE
      : conflicts.map((item) => item.details).join(' · '),
});

/**
 * One soldier, in one slot. The vehicle is only consulted for the driver — an
 * escort or a passenger needs no licence for the vehicle they ride in.
 */
export function checkSoldierAssignment(
  soldier: Soldier,
  vehicle: Vehicle | null,
  crewRole: CrewRole,
  context: ConflictContext = {},
): AssignmentCheck {
  const conflicts = [
    ...checkSoldierTimeConflicts(soldier.id, context),
    ...(crewRole === 'driver' && vehicle ? checkLicenseMatch(soldier, vehicle, context) : []),
    ...checkSoldierStatus(soldier, context),
  ];
  return verdict(conflicts.map((item) => ({ ...item, crewRole: item.crewRole ?? crewRole })));
}

/** One vehicle: is it free in the window, and is it in a state to go out. */
export function checkVehicleAssignment(
  vehicle: Vehicle,
  context: ConflictContext = {},
): AssignmentCheck {
  return verdict([
    ...checkVehicleTimeConflicts(vehicle.id, context),
    ...checkVehicleStatus(vehicle, context),
  ]);
}

// ── The availability light (נורית זמינות) ────────────────────────────

/**
 * The light beside a vehicle in the selection list: is this vehicle free for
 * the window the task is asking for, or is it already out on an overlapping
 * one?
 *
 * Only the schedule decides it. A grounded or in-the-workshop vehicle is still
 * *free* by this light — its own state is a different warning, raised by
 * {@link checkVehicleStatus} and shown in the banner — because the question the
 * light answers is the one a manager asks while reading the list: "is anybody
 * already using it at ten o'clock?".
 */
export type VehicleAvailability = 'free' | 'busy';

/** The two dots, spelled once so every list draws the same light. */
export const VEHICLE_AVAILABILITY_DOTS: Record<VehicleAvailability, string> = {
  free: '🟢',
  busy: '🔴',
};

export const VEHICLE_AVAILABILITY_LABELS: Record<VehicleAvailability, string> = {
  free: 'פנוי בזמן המבוקש',
  busy: 'משובץ למשימה חופפת',
};

/** The legend under the selector, so a red dot is never a mystery. */
export const VEHICLE_AVAILABILITY_LEGEND =
  '🟢 פנוי בזמן המבוקש · 🔴 משובץ למשימה חופפת — ניתן לבחור בכל זאת';

/** One vehicle's light, with the booking that turned it red. */
export interface VehicleAvailabilityLight {
  vehicleId: string;
  state: VehicleAvailability;
  dot: string;
  /** `פנוי בזמן המבוקש`, or the clash itself — what the option's tooltip says. */
  label: string;
  /** Every overlapping booking found, for the banner the form already shows. */
  conflicts: ScheduleConflict[];
}

/**
 * The light for one vehicle. Without a task window nothing can be judged, so
 * the vehicle reads as free — which is exactly what the form shows before a
 * date and a time have been chosen.
 */
export function vehicleAvailabilityLight(
  vehicleId: string,
  context: ConflictContext = {},
): VehicleAvailabilityLight {
  const conflicts = checkVehicleTimeConflicts(vehicleId, context);
  const state: VehicleAvailability = conflicts.length > 0 ? 'busy' : 'free';
  return {
    vehicleId,
    state,
    dot: VEHICLE_AVAILABILITY_DOTS[state],
    label:
      state === 'free'
        ? VEHICLE_AVAILABILITY_LABELS.free
        : conflicts.map((conflict) => conflict.details).join(' · '),
    conflicts,
  };
}

/** True when the vehicle is already out on a task overlapping this window. */
export function isVehicleBusy(vehicleId: string, context: ConflictContext = {}): boolean {
  return vehicleAvailabilityLight(vehicleId, context).state === 'busy';
}

/**
 * The whole list at once, keyed by vehicle id — one pass over the schedule for
 * a dropdown rather than one pass per option.
 */
export function vehicleAvailabilityLights(
  vehicles: readonly Vehicle[],
  context: ConflictContext = {},
): Map<string, VehicleAvailabilityLight> {
  const lights = new Map<string, VehicleAvailabilityLight>();
  for (const vehicle of vehicles) {
    lights.set(vehicle.id, vehicleAvailabilityLight(vehicle.id, context));
  }
  return lights;
}

// ── Team-level check ─────────────────────────────────────────────────

/** The team as the assignment form currently holds it. */
export interface TeamAssignmentInput {
  vehicleId: string;
  driverId?: string;
  escortId?: string;
  passengerIds?: readonly string[];
}

export interface TeamConflictContext extends ConflictContext {
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
}

const find = <T extends { id: string }>(items: readonly T[], id: string | undefined): T | null =>
  id ? (items.find((item) => item.id === id) ?? null) : null;

/**
 * The whole team in one pass — the vehicle, the driver, the escort and every
 * passenger — deduplicated by conflict key, so one banner can speak for the
 * form and one override can answer each finding exactly once.
 */
export function checkTeamAssignment(
  input: TeamAssignmentInput,
  context: TeamConflictContext,
): AssignmentCheck {
  const vehicle = find(context.vehicles, input.vehicleId);
  const collected: ScheduleConflict[] = [];

  if (vehicle) collected.push(...checkVehicleAssignment(vehicle, context).conflicts);

  const people: Array<[string | undefined, CrewRole]> = [
    [input.driverId, 'driver'],
    [input.escortId, 'escort'],
    ...(input.passengerIds ?? []).map(
      (id) => [id, 'passenger'] as [string | undefined, CrewRole],
    ),
  ];

  for (const [soldierId, crewRole] of people) {
    const soldier = find(context.soldiers, soldierId);
    if (!soldier) continue;
    collected.push(...checkSoldierAssignment(soldier, vehicle, crewRole, context).conflicts);
  }

  const seen = new Set<string>();
  const unique: ScheduleConflict[] = [];
  for (const item of collected) {
    if (seen.has(item.key)) continue;
    seen.add(item.key);
    unique.push(item);
  }
  return verdict(unique);
}

// ── Overrides ────────────────────────────────────────────────────────

export interface OverrideInput {
  reason: OverrideReasonCode | '';
  /** Mandatory when the reason is `OTHER`. */
  reasonText?: string;
  /** The `User.id` of the manager taking responsibility. */
  managerId: string;
  now?: number;
}

/** Why an override cannot be recorded yet, or `null` when it can. */
export function overrideInputError(input: OverrideInput): string | null {
  if (!input.reason) return 'יש לבחור סיבה לשיבוץ בכל זאת.';
  if (!OVERRIDE_REASON_LABELS[input.reason]) return 'הסיבה שנבחרה אינה מוכרת.';
  if (input.reason === OVERRIDE_REASON_REQUIRING_TEXT && !String(input.reasonText ?? '').trim()) {
    return 'בבחירת "סיבה אחרת" יש לפרט את הסיבה.';
  }
  if (!input.managerId) return 'לא ידוע מי המשבץ — נדרשת התחברות.';
  return null;
}

const newOverrideId = (): string => `ovr-${crypto.randomUUID()}`;

/**
 * The record of one manager answering one warning. Returns `null` when the
 * reason is missing or an "אחרת" was left unexplained — the requirement is
 * enforced here, once, rather than in the dialog.
 */
export function buildAssignmentOverride(
  conflict: ScheduleConflict,
  input: OverrideInput,
): AssignmentOverride | null {
  if (overrideInputError(input)) return null;
  const text = String(input.reasonText ?? '').trim();
  return {
    id: newOverrideId(),
    conflictKey: conflict.key,
    conflictType: conflict.conflictType,
    severity: conflict.severity,
    subjectType: conflict.subjectType,
    subjectId: conflict.subjectId,
    details: conflict.details,
    reason: input.reason as OverrideReasonCode,
    reasonText: text || undefined,
    managerId: input.managerId,
    createdAt: new Date(input.now ?? Date.now()).toISOString(),
  };
}

/** One override per warning, so each decision is auditable on its own. */
export function buildAssignmentOverrides(
  conflicts: readonly ScheduleConflict[],
  input: OverrideInput,
): AssignmentOverride[] {
  return conflicts.flatMap((conflict) => {
    const built = buildAssignmentOverride(conflict, input);
    return built ? [built] : [];
  });
}

export function isOverridden(
  conflict: ScheduleConflict,
  overrides: readonly AssignmentOverride[] = [],
): boolean {
  return overrides.some((override) => override.conflictKey === conflict.key);
}

/** The findings still waiting for a manager to take responsibility. */
export function unresolvedConflicts(
  conflicts: readonly ScheduleConflict[],
  overrides: readonly AssignmentOverride[] = [],
): ScheduleConflict[] {
  return conflicts.filter((conflict) => !isOverridden(conflict, overrides));
}

/** Drops overrides whose warning is gone — a changed driver takes theirs along. */
export function pruneOverrides(
  overrides: readonly AssignmentOverride[],
  conflicts: readonly ScheduleConflict[],
): AssignmentOverride[] {
  const live = new Set(conflicts.map((conflict) => conflict.key));
  return overrides.filter((override) => live.has(override.conflictKey));
}

/** The banner's one-liner: the green sentence, or the count of open warnings. */
export function describeAssignmentCheck(
  check: AssignmentCheck,
  overrides: readonly AssignmentOverride[] = [],
): string {
  if (!check.hasConflict) return CLEAR_ASSIGNMENT_MESSAGE;
  const open = unresolvedConflicts(check.conflicts, overrides).length;
  if (open === 0) return `כל ההתראות (${check.conflicts.length}) אושרו בשיבוץ בכל זאת`;
  return `נמצאו ${open} התראות שיבוץ`;
}
